import { ButtonHTMLAttributes, useEffect, type FC, type KeyboardEvent } from 'react';
import { HiOutlineClock, HiOutlineClipboardList } from 'react-icons/hi';

import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import useStartYourApplicationEditableAsset from '@core/hooks/useStartYourApplicationEditableAsset';
import type { VoyagerResult } from '@core/ts/results';

import ApplicationCtaButton from '../CtaButtons/ApplicationCtaButton';
import Pill from '../Pill';
import styles from './ApplicationCTA.module.css';

type Props = {
  cta: {
    url: string;
  };
  isLoading?: boolean;
  result: VoyagerResult;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const ApplicationCTA: FC<Props> = ({ cta, isLoading, result }) => {
  const { SchoolEnrollmentStatusEA, CtaLabelEA, CtaTitleEA, CtaSubtitleEA, ctaLabelCopyEA } =
    useStartYourApplicationEditableAsset(result?.school.id.toString());

  const { elementViewed, elementClicked } = useElementEvents({
    correlationId: result.eventing?.viewCorrelationId,
    webElement: {
      elementType: 'linkout-cta',
      text: ctaLabelCopyEA || 'Start My Application',
      location: 'drawer',
      htmlId: 'voyager-application-cta',
      name: 'application-cta',
    },
  });

  const onCtaClick = () => {
    elementClicked();
  };

  const handleCtaEnter = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      onCtaClick?.();
    }
  };

  useEffect(() => {
    if (cta?.url) {
      elementViewed();
    }
  }, [cta, elementViewed]);

  return (
    (cta && cta?.url !== '' && (
      <div className={styles.applicationCTAContainer}>
        <Pill className={styles.pill}>
          <HiOutlineClock />
          <span>
            <SchoolEnrollmentStatusEA>Accepting Students Now</SchoolEnrollmentStatusEA>
          </span>
        </Pill>
        <div className={styles.cta}>
          <HiOutlineClipboardList />
          <span>
            <CtaTitleEA>Apply Online Today</CtaTitleEA>
          </span>
        </div>
        <p>
          <CtaSubtitleEA>
            Online applications are fast, convenient, and let you track your acceptance status. Start yours today!
          </CtaSubtitleEA>
        </p>

        <ApplicationCtaButton
          className={styles.ctaButton}
          onClick={() => {
            window.open(cta?.url, '_blank');
          }}
          isLoading={isLoading}
        >
          <div className={styles.ctaLabel} role="button" onClick={onCtaClick} onKeyDown={handleCtaEnter} tabIndex={0}>
            <CtaLabelEA>Start Your Application</CtaLabelEA>
          </div>{' '}
          +
        </ApplicationCtaButton>
      </div>
    )) ||
    null
  );
};
export default ApplicationCTA;
